import React, { useState, useEffect, useMemo } from 'react';
import { listGroups, createGroup, updateGroup, deleteGroup, addStoresToGroup, assignStoresToGroup } from '../../api/groups';
import { listStores } from '../../api/stores';
import { getRates, postRates } from '../../api/rates';
import {
  getAllowances,
  createAllowance,
  updateAllowance,
  deleteAllowance,
  assignToGroup,
  removeGroupAssignment
} from '../../api/allowances';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  CircularProgress,
  Skeleton,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Fade,
  Pagination,
  Select,
  MenuItem,
  Alert,
  TableSortLabel,
  TablePagination,
  Popover,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  FormGroup,
  FormControlLabel,
  Divider,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel as MuiFormControlLabel,
  Switch,
  Autocomplete,
  Chip,
  TextField as MuiTextField,
  Tabs,
  Tab,
  Box as MuiBox,
  RadioGroup,
  Radio
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Store as StoreIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';
import { exportToCSV, exportToPrint, exportToPDF, exportToExcel, formatDataForExport } from '../../utils/exportUtils';
import { dedupeStoresById, filterStoreOptions } from '../../utils/Group';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';

const normalizeAccessValue = (value) => String(value || '').trim().toLowerCase();

const normalizeAccessKey = (value) => normalizeAccessValue(value)
  .replace(/\bphilippines\b/g, 'phils')
  .replace(/\bcorporation\b/g, 'corp')
  .replace(/\bincorporated\b/g, 'inc')
  .replace(/[^a-z0-9]/g, '');

const accessMatches = (accessValues, candidate) => {
  const normalizedCandidate = normalizeAccessValue(candidate);
  if (!normalizedCandidate) return false;

  const candidateKey = normalizeAccessKey(candidate);
  return accessValues.some((accessValue) => {
    const accessKey = normalizeAccessKey(accessValue);
    return normalizedCandidate === accessValue ||
      normalizedCandidate.includes(accessValue) ||
      accessValue.includes(normalizedCandidate) ||
      (candidateKey && accessKey && candidateKey === accessKey);
  });
};

const Groups = () => {
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // State for groups data from API
  const [groups, setGroups] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refreshing, setRefreshing] = useState(false);

  // State for stores data
  const [allStores, setAllStores] = useState([]);
  const [groupStores, setGroupStores] = useState({}); // Map of groupId to stores array

  // Enhanced table state
  const [page, setPage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [dense, setDense] = useState(false);

  // Filtering state
  const [filters, setFilters] = useState({
    status: []
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterColumn, setFilterColumn] = useState(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 for Group Info, 1 for Rates, 2 for Allowances
  
  // Allowances state
  const [allowances, setAllowances] = useState([]);
  const [groupAllowances, setGroupAllowances] = useState([]);
  const [allowanceDialogOpen, setAllowanceDialogOpen] = useState(false);
  const [allowanceDialogMode, setAllowanceDialogMode] = useState('add'); // 'add' or 'edit'
  const [selectedAllowance, setSelectedAllowance] = useState(null);
  const [allowanceFormData, setAllowanceFormData] = useState({
    name: '',
    type: 'billing',
    amount: 0,
    rate: 0,
    description: '',
    is_active: true,
    is_taxable: true,
    admin: true,
  });
  const [allowanceFormErrors, setAllowanceFormErrors] = useState({});
  const [loadingAllowances, setLoadingAllowances] = useState(false);
  const [allowanceSearchTerm, setAllowanceSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rebate: 0,
    isActive: true,
    selectedStores: [] // Array of selected store objects
  });
  const [ratesData, setRatesData] = useState({
    dailyRate: 0,
    holidayRate: 0,
    holidayOtRate: 0,
    holidayRestDayRate: 0,
    holidayRestDayOtRate: 0,
    nightshiftRate: 0,
    nightshiftOtRate: 0,
    holidaynightshiftRate: 0,
    holidaynightshiftOtRate: 0,
    specialnightshiftOtRate: 0,
    specialnightshiftRate: 0,
    overtimeRate: 0,
    sssRate: 0,
    pagibigRate: 0,
    philhealthRate: 0,
    restDayDutyRate: 0,
    restDayOtRate: 0,
    specialHolidayRate: 0,
    specialHolidayOtRate: 0,
    specialHolidayRdRate: 0,
    specialHolidayRdOtRate: 0,
    allowance: 0,
    lateEnabled: false,
    undertimeEnabled: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [ratesErrors, setRatesErrors] = useState({});
  const [saving, setSaving] = useState(false);
  
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super-admin' || user?.role === "admin";
  const groupAccessValues = useMemo(
    () => (Array.isArray(user?.groupAccess) ? user.groupAccess.map(normalizeAccessValue).filter(Boolean) : []),
    [user]
  );
  const storeAccessValues = useMemo(
    () => (Array.isArray(user?.storeAccess) ? user.storeAccess.map(normalizeAccessValue).filter(Boolean) : []),
    [user]
  );
  const currentUserId = String(user?._id || user?.id || '');

  const isGroupCreator = (group) => {
    const groupUserId = String(group?.user_id || '');
    return Boolean(currentUserId && groupUserId && currentUserId === groupUserId);
  };

  const userCanAccessGroup = (group) => (
    isSuperAdmin ||
    isGroupCreator(group) ||
    accessMatches(groupAccessValues, group?.name) ||
    (group?.stores || []).some((store) => accessMatches(storeAccessValues, store?.store))
  );

  const userCanAccessStore = (store, group) => (
    isSuperAdmin ||
    isGroupCreator(group) ||
    accessMatches(groupAccessValues, group?.name) ||
    accessMatches(storeAccessValues, store?.store || store?.name)
  );

  // Load stores from API
  const loadStores = async () => {
    try {
      const response = await listStores();
      if (response.status === 'success') {
        const transformedStores = dedupeStoresById(response.data.stores || []);
        setAllStores(transformedStores);
      }
    } catch (err) {
      console.error('Error loading stores:', err);
    }
  };

  // Get available stores (not assigned to other groups)
  const getAvailableStores = (currentGroupId = null) => {
    return allStores.filter(store => {
      // Include stores that are not assigned to any group or assigned to the current group
      return !store.group_id || store.group_id === '' || store.group_id === currentGroupId;
    });
  };

  // Get group name by ID
  const getGroupNameById = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    return group ? group.name : 'Unknown Group';
  };


  // Load groups from API (now includes store counts and all data)
  const loadGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      await refreshTableData(); // Now handles groups, stores, and group stores in one call
    } catch (err) {
      console.error('Error loading groups:', err);
      setError(err.message || 'Failed to load groups');
      enqueueSnackbar(err.message || 'Failed to load groups', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Refresh only table data without full page loading
  const refreshTableData = async () => {
    setTableLoading(true);
    try {
      const response = await listGroups();

      if (response.status === 'success') {
        const allowedGroups = (response.data.groups || []).filter(userCanAccessGroup);
        console.log(allowedGroups)
        // Transform groups to match the expected format
        const transformedGroups = allowedGroups.map((group) => {
          const accessibleStores = (group.stores || []).filter((store) => userCanAccessStore(store, group));

          return {
            id: group.id, // Use the id field (string) instead of _id
            name: group.name,
            storeCount: accessibleStores.length,
            active: group.isActive,
            description: group.description || '',
            rebate: Number(group.rebate) || 0,
            created_at: group.created_at,
            updated_at: group.updated_at,
            user_id: group.user_id
          };
        });

        // Transform all stores for autocomplete functionality
        const transformedAllStores = dedupeStoresById(response.data.allStores || []);

        // Set groups data
        setGroups(transformedGroups);

        // Set all stores for autocomplete
        setAllStores(transformedAllStores);

        // Set group stores from the consolidated response
        const newGroupStores = {};
        allowedGroups.forEach((group) => {
          const groupId = group.id; // Use the id field (string) instead of _id
          const transformedGroupStores = dedupeStoresById(group.stores || []);
          newGroupStores[groupId] = transformedGroupStores;
        });
        setGroupStores(newGroupStores);
      }
    } catch (err) {
      console.error('Error refreshing groups:', err);
      enqueueSnackbar('Failed to refresh data', { variant: 'error' });
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, [enqueueSnackbar, user]);

  // Sorting utility functions - moved to top to avoid initialization issues
  const descendingComparator = (a, b, orderBy) => {
    try {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return bValue - aValue;
      }

      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return bValue === aValue ? 0 : (bValue ? 1 : -1);
      }

      // Fallback to string comparison
      const aStr = String(aValue || '');
      const bStr = String(bValue || '');
      return bStr.localeCompare(aStr);
    } catch (error) {
      console.error('Error in descendingComparator:', error);
      return 0;
    }
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  // Filter handler
  const handleFilterClick = (event, column) => {
    setFilterAnchorEl(event.currentTarget);
    setFilterColumn(column);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setFilterColumn(null);
  };

  const handleFilterChange = (column, value) => {
    setFilters(prev => ({
      ...prev,
      [column]: prev[column].includes(value)
        ? prev[column].filter(item => item !== value)
        : [...prev[column], value]
    }));
  };

  const clearFilters = () => {
    setFilters({ status: [] });
    setSearchTerm('');
    setCurrentPage(1);
    setPage(0);
  };

  // Get unique values for filters
  const uniqueStatuses = ['Active', 'Inactive'];

  // Enhanced filter, sort and pagination logic
  const filteredGroups = useMemo(() => {
    return groups.filter(group => {
      // Text search
      const matchesSearch = !searchTerm ||
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.storeCount.toString().includes(searchTerm) ||
        group.rebate.toString().includes(searchTerm);

      // Status filter
      const matchesStatus = filters.status.length === 0 ||
        filters.status.includes(group.active ? 'Active' : 'Inactive');

      return matchesSearch && matchesStatus;
    });
  }, [searchTerm, groups, filters]);

  // Apply sorting
  const sortedGroups = useMemo(() => {
    return stableSort(filteredGroups, getComparator(order, orderBy));
  }, [filteredGroups, order, orderBy]);

  // Apply pagination for enhanced table
  const paginatedSortedGroups = useMemo(() => {
    return sortedGroups.slice(page * tableRowsPerPage, page * tableRowsPerPage + tableRowsPerPage);
  }, [sortedGroups, page, tableRowsPerPage]);

  // Legacy pagination for existing components (using sorted data)
  const totalPages = Math.ceil(sortedGroups.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedGroups = sortedGroups.slice(startIndex, startIndex + rowsPerPage);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshTableData();
      enqueueSnackbar('Data refreshed successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error refreshing groups:', err);
      enqueueSnackbar('Failed to refresh data', { variant: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  // Enhanced table handlers
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setTableRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = async (type) => {
    try {
      const dataToExport = formatDataForExport(filteredGroups, 'groups');

      switch (type) {
        case 'CSV':
          exportToCSV(dataToExport, 'groups_export');
          enqueueSnackbar('CSV exported successfully', { variant: 'success' });
          break;
        case 'Print':
          exportToPrint(dataToExport, 'Groups Report');
          enqueueSnackbar('Print dialog opened', { variant: 'info' });
          break;
        case 'PDF':
          exportToPDF(dataToExport, 'groups_export', 'Groups Report');
          enqueueSnackbar('PDF export initiated', { variant: 'info' });
          break;
        case 'Excel':
          const success = await exportToExcel(dataToExport, 'groups_export', 'Groups Report');
          if (success) {
            enqueueSnackbar('Excel exported successfully', { variant: 'success' });
          }
          break;
        default:
          enqueueSnackbar(`Unknown export type: ${type}`, { variant: 'warning' });
      }
    } catch (error) {
      console.error('Export error:', error);
      enqueueSnackbar('Export failed', { variant: 'error' });
    }
  };

  // Handle delete group
  const handleDeleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`Are you sure you want to delete "${groupName}"? This will also remove all associated stores.`)) {
      return;
    }

    try {
      const response = await deleteGroup(groupId);
      if (response.status === 'success') {
        // Remove from local state
        setGroups(prevGroups => prevGroups.filter(group => group.id !== groupId));
        enqueueSnackbar(`Group "${groupName}" deleted successfully`, { variant: 'success' });
      } else {
        throw new Error(response.message || 'Failed to delete group');
      }
    } catch (err) {
      console.error('Error deleting group:', err);
      enqueueSnackbar(err.message || 'Failed to delete group', { variant: 'error' });
    }
  };

  // Handle edit group
  const handleEditGroup = (groupId, groupName) => {
    const group = groups.find(g => g.id === groupId);
    if (group) {
      handleOpenDialog('edit', group);
    } else {
      enqueueSnackbar('Group not found', { variant: 'error' });
    }
  };

  // Dialog handlers
  const handleOpenDialog = async (mode = 'add', group = null) => {
    setDialogMode(mode);
    setSelectedGroup(group);
    setActiveTab(0); // Reset to first tab

    if (mode === 'edit' && group) {
      // Load rates data for the group when editing
      const data = {
        id: group.id
      }
      const rates = await getRates(data);

      // Group stores are now loaded from the consolidated /api/groups response
      setFormData({
        name: group.name || '',
        description: group.description || '',
        rebate: Number(group.rebate) || 0,
        isActive: group.active,
        selectedStores: dedupeStoresById(groupStores[group.id] || [])
      });

      // Load rates data for the group
      setRatesData(rates.data);
    } else {
      setFormData({
        name: '',
        description: '',
        rebate: 0,
        isActive: true,
        selectedStores: []
      });
      setRatesData({
        dailyRate: 0,
        holidayRate: 0,
        holidayOtRate: 0,
        holidayRestDayRate: 0,
        holidayRestDayOtRate: 0,
        nightshiftRate: 0,
        overtimeRate: 0,
        sssRate: 0,
        pagibigRate: 0,
        philhealthRate: 0,
        restDayDutyRate: 0,
        restDayOtRate: 0,
        specialHolidayRate: 0,
        specialHolidayOtRate: 0,
        specialHolidayRdRate: 0,
        specialHolidayRdOtRate: 0,
        allowance: 0,
        lateEnabled: false,
        undertimeEnabled: false
      });
    }

    setFormErrors({});
    setRatesErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedGroup(null);
    setActiveTab(0);
    setFormData({
      name: '',
      description: '',
      rebate: 0,
      isActive: true,
      selectedStores: []
    });
    setRatesData({
      dailyRate: 0,
      holidayRate: 0,
      holidayOtRate: 0,
      holidayRestDayRate: 0,
      holidayRestDayOtRate: 0,
      nightshiftRate: 0,
      overtimeRate: 0,
      sssRate: 0,
      pagibigRate: 0,
      philhealthRate: 0,
      restDayDutyRate: 0,
      restDayOtRate: 0,
      specialHolidayRate: 0,
      specialHolidayOtRate: 0,
      specialHolidayRdRate: 0,
      specialHolidayRdOtRate: 0,
      allowance: 0,
      lateEnabled: false,
      undertimeEnabled: false
    });
    setFormErrors({});
    setRatesErrors({});
    setGroupAllowances([]);
    setAllowanceSearchTerm('');
  };

  // Load all allowances
  const loadAllowances = async () => {
    try {
      setLoadingAllowances(true);
      const data = {
        group:selectedGroup.name
      }
      const response = await getAllowances(data);
      if (response.status === 'success') {
        setAllowances(response.data || []);
      }
    } catch (error) {
      console.error('Error loading allowances:', error);
      enqueueSnackbar('Failed to load allowances', { variant: 'error' });
    } finally {
      setLoadingAllowances(false);
    }
  };

  // Load allowances assigned to current group
  const loadGroupAllowances = async (groupId) => {
    if (!groupId) return;
    try {
      const response = await getAllowances({ group_id: groupId });
      if (response.status === 'success') {
        // Filter allowances that are assigned to this group
        const allAllowances = response.data || [];
        const assignedAllowances = allAllowances.filter(allowance => 
          allowance.assignments?.groups?.includes(groupId)
        );
        setGroupAllowances(assignedAllowances);
      }
    } catch (error) {
      console.error('Error loading group allowances:', error);
    }
  };

  // Load allowances when tab changes to Allowances
  useEffect(() => {
    if (activeTab === 2 && dialogMode === 'edit' && selectedGroup) {
      loadAllowances();
      loadGroupAllowances(selectedGroup.id);
    }
  }, [activeTab, dialogMode, selectedGroup?.id]);

  // Handle allowance dialog open
  const handleOpenAllowanceDialog = (mode = 'add', allowance = null) => {
    setAllowanceDialogMode(mode);
    if (mode === 'edit' && allowance) {
      setSelectedAllowance(allowance);
      setAllowanceFormData({
        name: allowance.name || '',
        type: allowance.type || 'billing',
        amount: allowance.amount || 0,
        rate: allowance.rate || 0,
        description: allowance.description || '',
        is_active: allowance.is_active !== undefined ? allowance.is_active : true,
        is_taxable: allowance.is_taxable !== undefined ? allowance.is_taxable : true,
        admin: allowance.admin !== undefined ? allowance.admin : true,

      });
    } else {
      setSelectedAllowance(null);
      setAllowanceFormData({
        name: '',
        type: 'billing',
        amount: 0,
        rate: 0,
        description: '',
        is_active: true,
        is_taxable: true,
        admin: true
      });
    }
    setAllowanceFormErrors({});
    setAllowanceDialogOpen(true);
  };

  // Handle allowance dialog close
  const handleCloseAllowanceDialog = () => {
    setAllowanceDialogOpen(false);
    setSelectedAllowance(null);
    setAllowanceFormData({
      name: '',
      type: 'fixed_amount',
      amount: 0,
      rate: 0,
      description: '',
      is_active: true,
      is_taxable: true
    });
    setAllowanceFormErrors({});
  };

  // Validate allowance form
  const validateAllowanceForm = () => {
    const errors = {};
    if (!allowanceFormData.name.trim()) {
      errors.name = 'Allowance name is required';
    }
    if (allowanceFormData.type === 'fixed_amount' && (!allowanceFormData.amount || allowanceFormData.amount <= 0)) {
      errors.amount = 'Amount must be greater than 0';
    }
    if (allowanceFormData.type === 'rate_based' && (!allowanceFormData.rate || allowanceFormData.rate <= 0)) {
      errors.rate = 'Rate must be greater than 0';
    }
    setAllowanceFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle allowance form submit
  const handleAllowanceFormSubmit = async () => {
    if (!validateAllowanceForm()) return;
    setSaving(true);
    try {
      console.log(allowanceFormData)
      const allowanceData = {
        name: allowanceFormData.name,
        type: allowanceFormData.type,
        description: allowanceFormData.description,
        is_active: allowanceFormData.is_active,
        is_taxable: allowanceFormData.is_taxable,
        admin: allowanceFormData.admin,
        amount: allowanceFormData.amount,
        assignments: {
          personnel: [],
          stores: [],
          groups: selectedGroup.name
        }
      };

      // if (allowanceFormData.type === 'fixed_amount') {
      //   allowanceData.amount = allowanceFormData.amount;
      // } else {
      //   allowanceData.rate = allowanceFormData.rate;
      // }

      let allowanceId;
      if (allowanceDialogMode === 'add') {
        const response = await createAllowance(allowanceData);
        if (response.status === 'success') {
          allowanceId = response.data._id;
          enqueueSnackbar('Allowance created successfully', { variant: 'success' });
        }
      } else {
        await updateAllowance(selectedAllowance._id, allowanceData);
        allowanceId = selectedAllowance._id;
        enqueueSnackbar('Allowance updated successfully', { variant: 'success' });
      }

      // If we're in edit mode for a group, assign the allowance to the group
      if (allowanceId && dialogMode === 'edit' && selectedGroup) {
        try {
          await assignToGroup(allowanceId, [selectedGroup.id]);
        } catch (assignError) {
          console.error('Error assigning allowance to group:', assignError);
        }
      }

      await loadAllowances();
      if (dialogMode === 'edit' && selectedGroup) {
        await loadGroupAllowances(selectedGroup.id);
      }
      handleCloseAllowanceDialog();
    } catch (error) {
      console.error('Error saving allowance:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save allowance';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle delete allowance
  const handleDeleteAllowance = async (allowance) => {
    if (!window.confirm(`Are you sure you want to delete "${allowance.name}"?`)) {
      return;
    }

    try {
      await deleteAllowance(allowance._id);
      enqueueSnackbar('Allowance deleted successfully', { variant: 'success' });
      await loadAllowances();
      if (dialogMode === 'edit' && selectedGroup) {
        await loadGroupAllowances(selectedGroup.id);
      }
    } catch (error) {
      console.error('Error deleting allowance:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete allowance';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Handle assign/unassign allowance to group
  const handleToggleAllowanceAssignment = async (allowance) => {
    if (!selectedGroup) return;

    const isAssigned = allowance.assignments?.groups?.includes(selectedGroup.id);
    
    try {
      if (isAssigned) {
        await removeGroupAssignment(allowance._id, selectedGroup.id);
        enqueueSnackbar('Allowance unassigned from group', { variant: 'success' });
      } else {
        await assignToGroup(allowance._id, [selectedGroup.id]);
        enqueueSnackbar('Allowance assigned to group', { variant: 'success' });
      }
      await loadAllowances();
      await loadGroupAllowances(selectedGroup.id);
    } catch (error) {
      console.error('Error toggling allowance assignment:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update assignment';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Group name is required';
    }
    const rebate = Number(formData.rebate);
    if (!Number.isFinite(rebate) || rebate < 0) {
      errors.rebate = 'Rebate must be a non-negative amount';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateRatesForm = () => {
    const errors = {};
    // Add validation for rates if needed
    setRatesErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTabChange = async (event, newValue) => {
    setActiveTab(newValue);
    // Load allowances when switching to Allowances tab
    if (newValue === 2 && dialogMode === 'edit' && selectedGroup) {
      await loadAllowances();
      await loadGroupAllowances(selectedGroup.id);
    }
  };

  const handleRatesInputChange = (field, value) => {
    if (value === '' && Number(value) <= 0) {
      return;
    }
    setRatesData(prev => ({
      ...prev,
      [field]: (field === "late" || field === "undertime") 
        ? (value ? 1 : null)   // convert boolean → 1/null
        : value
    }));

    // Clear error when user starts typing/toggling
    if (ratesErrors[field]) {
      setRatesErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const handleFormSubmit = async () => {
    if (activeTab === 0) {
      // Group Info tab
      if (!validateForm()) return;

      setSaving(true);
      try {
        let groupId;

        if (dialogMode === 'add') {
          const groupResponse = await createGroup({
            name: formData.name,
            description: formData.description,
            rebate: Number(formData.rebate) || 0,
            isActive: formData.isActive
          });
          enqueueSnackbar('Group created successfully', { variant: 'success' });

          // Get the created group ID (now using the id field which is a string)
          if (groupResponse.status === 'success' && groupResponse.data) {
            groupId = groupResponse.data.id; // Use the id field (string) from response
          }
        } else if (dialogMode === 'edit' && selectedGroup) {
          await updateGroup(selectedGroup.id, {
            name: formData.name,
            description: formData.description,
            rebate: Number(formData.rebate) || 0,
            isActive: formData.isActive
          });
          groupId = selectedGroup.id;
          enqueueSnackbar('Group updated successfully', { variant: 'success' });
        }

        // Assign/unassign stores to the group
        if (groupId) {
          const storeIds = formData.selectedStores.length > 0
            ? formData.selectedStores.map(store => store.id)
            : []; // Empty array to unassign all stores
          const assignResponse = await assignStoresToGroup(groupId, { stores: storeIds });

          if (assignResponse.status === 'success') {
            const { assigned, unassigned, errors } = assignResponse.data;

            // Show success message with details
            let message = 'Store assignment completed';
            if (assigned.length > 0) message += ` - ${assigned.length} assigned`;
            if (unassigned.length > 0) message += ` - ${unassigned.length} unassigned`;
            if (errors.length > 0) message += ` - ${errors.length} errors`;

            enqueueSnackbar(message, { variant: 'success' });

            // Show errors if any
            if (errors.length > 0) {
              errors.forEach(error => {
                enqueueSnackbar(`Error: ${error.error}`, { variant: 'warning' });
              });
            }
          } else {
            enqueueSnackbar('Failed to update store assignments', { variant: 'error' });
          }
        }

        // Refresh only table data without full page loading
        await refreshTableData();
        handleCloseDialog();
      } catch (error) {
        console.error('Error saving group:', error);
        console.error('Error details:', error.response?.data || error.message);

        // Show more specific error message
        const errorMessage = error.response?.data?.message || error.message || 'Failed to save group';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setSaving(false);
      }
    } else {
      // Rates tab
      if (!validateRatesForm()) return;

      setSaving(true);
      try {
        const result = await postRates(ratesData)
        console.log('Saving rates:', result);
        enqueueSnackbar('Rates saved successfully', { variant: 'success' });
        
        // You can add API call here when backend is ready
        // await updateGroupRates(selectedGroup.id, ratesData);
        
        handleCloseDialog();
      } catch (error) {
        console.error('Error saving rates:', error);
        enqueueSnackbar('Failed to save rates', { variant: 'error' });
      } finally {
        setSaving(false);
      }
    }
  };

  const handleInputChange = (field, value) => {
    const nextValue = field === 'selectedStores'
      ? dedupeStoresById(value || [])
      : value;

    setFormData(prev => ({
      ...prev,
      [field]: nextValue
    }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle create group
  const handleCreateGroup = () => {
    handleOpenDialog('add');
  };
  
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="200px" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="300px" height={20} sx={{ mb: 3 }} />
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="rectangular" width="200px" height={36} />
            <Skeleton variant="rectangular" width="120px" height={36} />
          </Box>
          {[...Array(5)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" width="100%" height={60} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Paper>
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 2,
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              sx={{ fontWeight: 'bold', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <GroupIcon sx={{ color: '#12C1DC' }} />
              Masterfiles
            </Typography>
            <Tooltip title="Refresh data">
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  color: '#12C1DC',
                  '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                }}
              >
                {refreshing ? (
                  <CircularProgress size={20} sx={{ color: '#12C1DC' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link underline="hover" color="inherit" href="/masterfiles">
              Masterfiles
            </Link>
            <Typography color="text.primary">Groups</Typography>
          </Breadcrumbs>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          </motion.div>
        )}

        {/* Title and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 3,
            gap: { xs: 2, sm: 0 }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                component="h2"
                sx={{ fontWeight: 'bold', color: '#374151' }}
              >
                Groups
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search groups, IDs, store counts..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#9ca3af' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchTerm('');
                          setCurrentPage(1);
                          setPage(0);
                        }}
                        sx={{ color: '#9ca3af' }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  minWidth: { xs: 'auto', md: '300px' },
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#12C1DC' },
                    '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                  }
                }}
              />
              {filters.status.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  sx={{
                    color: '#12C1DC',
                    borderColor: '#12C1DC',
                    '&:hover': {
                      borderColor: '#0FABDB',
                      bgcolor: 'rgba(18, 193, 220, 0.1)'
                    }
                  }}
                >
                  Clear Filters
                  <Badge
                    badgeContent={filters.status.length}
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                </Button>
              )}
            </Box>
                          <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateGroup}
                sx={{
                  bgcolor: '#12C1DC',
                  '&:hover': { bgcolor: '#0FABDB' },
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 3,
                  py: 1
                }}
              >
                Add Group
              </Button>
          </Box>
        </motion.div>

        {/* Controls and Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* Table Controls */}
            <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderBottom: '1px solid #e5e7eb' }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    Show
                  </Typography>
                  <Select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(e.target.value);
                      setCurrentPage(1);
                    }}
                    size="small"
                    sx={{ minWidth: 80 }}
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                    entries
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-end' }
                }}>
                  <Tooltip title="Export CSV">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('CSV')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <ExportIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export Excel">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('Excel')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <Icon icon="mdi:microsoft-excel" width="18" height="18" style={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('Print')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <PrintIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export PDF">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('PDF')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <PdfIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {/* Enhanced Table */}
            <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto', position: 'relative' }}>
              {tableLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    borderRadius: 1
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={40} sx={{ color: '#12C1DC' }} />
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        Loading groups...
                      </Typography>
                    </Box>
                  </motion.div>
                </Box>
              )}
              <Table
                stickyHeader
                size={dense ? 'small' : (isMobile ? "small" : "medium")}
                aria-label="enhanced groups table"
              >
                <TableHead>
                  <TableRow sx={{
                    '& .MuiTableCell-root': {
                      backgroundColor: '#f9fafb',
                      borderBottom: '2px solid #e5e7eb'
                    }
                  }}>
                    <TableCell 
                      sortDirection={orderBy === 'id' ? order : false}
                      sx={{ 
                        fontWeight: 600, 
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'id'}
                        direction={orderBy === 'id' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'id')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell 
                      sortDirection={orderBy === 'name' ? order : false}
                      sx={{ 
                        fontWeight: 600, 
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'name')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Group Name
                      </TableSortLabel>
                    </TableCell>
                    {!isMobile && (
                      <TableCell
                        sortDirection={orderBy === 'rebate' ? order : false}
                        sx={{
                          fontWeight: 600,
                          bgcolor: '#f9fafb',
                          color: '#374151',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === 'rebate'}
                          direction={orderBy === 'rebate' ? order : 'asc'}
                          onClick={(event) => handleRequestSort(event, 'rebate')}
                        >
                          Rebate
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {!isMobile && (
                    <TableCell
                      sortDirection={orderBy === 'storeCount' ? order : false}
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'storeCount'}
                        direction={orderBy === 'storeCount' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'storeCount')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Store Count
                      </TableSortLabel>
                    </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          bgcolor: '#f9fafb',
                          color: '#374151',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}
                      >
                        Stores
                      </TableCell>
                    )}
                    <TableCell
                      sortDirection={orderBy === 'active' ? order : false}
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TableSortLabel
                          active={orderBy === 'active'}
                          direction={orderBy === 'active' ? order : 'asc'}
                          onClick={(event) => handleRequestSort(event, 'active')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: '#12C1DC !important',
                            },
                          }}
                        >
                          Status
                        </TableSortLabel>
                        <IconButton
                          size="small"
                          onClick={(e) => handleFilterClick(e, 'status')}
                          sx={{
                            color: filters.status.length > 0 ? '#12C1DC' : '#9ca3af',
                            p: 0.5,
                            '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                          }}
                        >
                          <Badge
                            badgeContent={filters.status.length}
                            color="primary"
                            variant="dot"
                            invisible={filters.status.length === 0}
                          >
                            <FilterListIcon fontSize="small" />
                          </Badge>
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ 
                      fontWeight: 600, 
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <AnimatePresence>
                    {paginatedGroups.map((group, index) => {
                      const isEvenRow = index % 2 === 0;
                      return (
                        <motion.tr
                          key={index}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                          style={{
                            display: 'table-row',
                            backgroundColor: isEvenRow ? '#ffffff' : '#f8f9fa'
                          }}
                          sx={{
                            '&:hover': {
                              backgroundColor: isEvenRow ? '#f0f0f0 !important' : '#e9ecef !important'
                            },
                            '& > .MuiTableCell-root': {
                              borderRight: '1px solid #e0e0e0',
                              borderBottom: '1px solid #e0e0e0'
                            },
                            '& > .MuiTableCell-root:last-child': {
                              borderRight: 'none'
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {group.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <GroupIcon sx={{ color: '#12C1DC', fontSize: '1.2rem' }} />
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {group.name}
                                </Typography>
                                {isMobile && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      {group.storeCount} stores
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      Rebate: ₱{Number(group.rebate || 0).toLocaleString('en-PH', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                      })}
                                    </Typography>
                                    {groupStores[group.id] && groupStores[group.id].length > 0 && (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                        {groupStores[group.id].slice(0, 2).map((store) => (
                                          <Chip
                                            key={store.id}
                                            label={store.name}
                                            size="small"
                                            sx={{
                                              fontSize: '0.6rem',
                                              height: '18px',
                                              bgcolor: '#f3f4f6',
                                              color: '#374151'
                                            }}
                                          />
                                        ))}
                                        {groupStores[group.id].length > 2 && (
                                          <Chip
                                            label={`+${groupStores[group.id].length - 2}`}
                                            size="small"
                                            sx={{
                                              fontSize: '0.6rem',
                                              height: '18px',
                                              bgcolor: '#e5e7eb',
                                              color: '#6b7280'
                                            }}
                                          />
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          {!isMobile && (
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                ₱{Number(group.rebate || 0).toLocaleString('en-PH', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                              </Typography>
                            </TableCell>
                          )}
                          {!isMobile && (
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <StoreIcon sx={{ color: '#6b7280', fontSize: '1rem' }} />
                                <Typography variant="body2">{group.storeCount}</Typography>
                              </Box>
                            </TableCell>
                          )}
                          {!isMobile && (
                            <TableCell>
                              <Box sx={{ minWidth: 200 }}>
                                <Autocomplete
                                  multiple
                                  id={`stores-autocomplete-${group.id}`}
                                  options={groupStores[group.id] || []}
                                  getOptionLabel={(option) => option?.name || ''}
                                  value={groupStores[group.id] || []}
                                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                  size="small"
                                  clearIcon={false}
                                  renderInput={(params) => (
                                    <MuiTextField
                                      {...params}
                                      placeholder={groupStores[group.id] && groupStores[group.id].length > 0 ? `${groupStores[group.id].length} stores selected` : "No stores assigned"}
                                      variant="standard"
                                      sx={{
                                        '& .MuiInput-root': {
                                          fontSize: '0.875rem',
                                          '&:before': { borderBottom: 'none' },
                                          '&:after': { borderBottom: 'none' },
                                          '&:hover:before': { borderBottom: 'none' },
                                        },
                                        '& .MuiInput-input': {
                                          padding: '4px 0',
                                        }
                                      }}
                                    />
                                  )}
                                  renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                      <Chip
                                        key={option.id}
                                        label={option.name}
                                        size="small"
                                        {...getTagProps({ index })}
                                        sx={{
                                          fontSize: '0.7rem',
                                          height: '20px',
                                          bgcolor: '#12C1DC',
                                          color: 'white',
                                          '& .MuiChip-deleteIcon': {
                                            display: 'none'
                                          }
                                        }}
                                      />
                                    ))
                                  }
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <StoreIcon sx={{ color: '#12C1DC', fontSize: '1rem' }} />
                                        <Box>
                                          <Typography variant="body2" fontWeight={600}>
                                            {option.name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {option.location}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Box>
                                  )}
                                  sx={{
                                    '& .MuiAutocomplete-popupIndicator': {
                                      color: '#12C1DC'
                                    },
                                    '& .MuiAutocomplete-clearIndicator': {
                                      color: '#12C1DC'
                                    }
                                  }}
                                  noOptionsText="No stores available"
                                  limitTags={3}
                                />
                              </Box>
                            </TableCell>
                          )}
                          <TableCell>
                            <Chip
                              label={group.active ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{
                                bgcolor: group.active ? '#dcfce7' : '#fef2f2',
                                color: group.active ? '#166534' : '#dc2626',
                                fontWeight: 600,
                                borderRadius: '12px'
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Tooltip title="Edit group">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditGroup(group.id, group.name)}
                                  sx={{
                                    color: '#12C1DC',
                                    '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete group">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteGroup(group.id, group.name)}
                                  sx={{
                                    color: '#ef4444',
                                    '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                gap: { xs: 2, sm: 0 }
              }}>
                <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                  Showing {startIndex + 1} to {Math.min(startIndex + rowsPerPage, filteredGroups.length)} of {filteredGroups.length} entries
                  {(searchTerm || filters.status.length > 0) && ` (filtered from ${groups.length} total entries)`}
                </Typography>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(event, page) => setCurrentPage(page)}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: '8px',
                      '&.Mui-selected': {
                        backgroundColor: '#12C1DC',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#0FABDB'
                        }
                      }
                    }
                  }}
                />
              </Box>
            )}

            {/* Filter Popover */}
            <Popover
              open={Boolean(filterAnchorEl)}
              anchorEl={filterAnchorEl}
              onClose={handleFilterClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <Box sx={{ p: 2, minWidth: 250 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Filter by Status
                </Typography>

                <FormGroup>
                  {uniqueStatuses.map((status) => (
                    <FormControlLabel
                      key={status}
                      control={
                        <Checkbox
                          checked={filters.status.includes(status)}
                          onChange={() => handleFilterChange('status', status)}
                          size="small"
                        />
                      }
                      label={
                        <Chip
                          label={status}
                          size="small"
                          sx={{
                            bgcolor: status === 'Active' ? '#dcfce7' : '#fef2f2',
                            color: status === 'Active' ? '#166534' : '#dc2626',
                            fontSize: '0.75rem',
                            height: '20px'
                          }}
                        />
                      }
                    />
                  ))}
                </FormGroup>

                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleFilterClose}
                    sx={{ flex: 1 }}
                  >
                    Close
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      setFilters(prev => ({ ...prev, status: [] }));
                    }}
                    sx={{
                      flex: 1,
                      bgcolor: '#12C1DC',
                      '&:hover': { bgcolor: '#0FABDB' }
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>
            </Popover>

            {/* Group Dialog */}
            <Dialog
              open={dialogOpen}
              onClose={handleCloseDialog}
              maxWidth={activeTab === 1 || activeTab === 2 ? "md" : "sm"}
              fullWidth
            >
              <DialogTitle>
                {dialogMode === 'add' ? 'Add New Group' : 'Edit Group'}
              </DialogTitle>
              
              {dialogMode === 'edit' && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={activeTab} onChange={handleTabChange} aria-label="group edit tabs">
                    <Tab label="Group Info" />
                    <Tab label="Rates" />
                    <Tab label="Allowances" />
                  </Tabs>
                </Box>
              )}

              <DialogContent>
                {activeTab === 0 ? (
                  // Group Info Tab
                  <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Group Name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      error={!!formErrors.name}
                      helperText={formErrors.name}
                      fullWidth
                      required
                      disabled={saving}
                    />

                    <TextField
                      label="Description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      multiline
                      rows={3}
                      fullWidth
                      disabled={saving}
                    />

                    <TextField
                      label="Rebate Amount"
                      type="number"
                      value={formData.rebate}
                      onChange={(e) => handleInputChange('rebate', e.target.value)}
                      error={!!formErrors.rebate}
                      helperText={formErrors.rebate || 'Fixed amount automatically applied when a payroll for this group is saved.'}
                      fullWidth
                      disabled={saving}
                      inputProps={{ min: 0, step: '0.01' }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₱</InputAdornment>
                      }}
                    />

                    <Autocomplete
                      multiple
                      id="stores-autocomplete"
                      options={getAvailableStores(dialogMode === 'edit' ? selectedGroup?.id : null)}
                      filterOptions={filterStoreOptions}
                      filterSelectedOptions
                      getOptionLabel={(option) => option?.name || ''}
                      value={formData.selectedStores}
                      onChange={(event, newValue) => handleInputChange('selectedStores', newValue)}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      getOptionDisabled={(option) => {
                        const currentGroupId = dialogMode === 'edit' ? selectedGroup?.id : null;
                        return option.group_id && option.group_id !== '' && option.group_id !== currentGroupId;
                      }}
                      disabled={saving}
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          label="Assign Stores"
                          placeholder="Select stores to assign to this group"
                          helperText={`${formData.selectedStores.length} store(s) selected`}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': { borderColor: '#12C1DC' },
                              '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                            }
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option.id}
                            label={option.name}
                            size="small"
                            {...getTagProps({ index })}
                            sx={{
                              bgcolor: '#12C1DC',
                              color: 'white',
                              '& .MuiChip-deleteIcon': {
                                color: 'white',
                                '&:hover': {
                                  color: '#e5e7eb'
                                }
                              }
                            }}
                          />
                        ))
                      }
                      renderOption={(props, option) => {
                        const currentGroupId = dialogMode === 'edit' ? selectedGroup?.id : null;
                        const isAssignedToOtherGroup = option.group_id && option.group_id !== '' && option.group_id !== currentGroupId;
                        
                        return (
                          <Box component="li" {...props}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <StoreIcon sx={{ color: isAssignedToOtherGroup ? '#9ca3af' : '#12C1DC', fontSize: '1rem' }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight={600} sx={{ color: isAssignedToOtherGroup ? '#9ca3af' : 'inherit' }}>
                                  {option.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.location}
                                </Typography>
                                {isAssignedToOtherGroup && (
                                  <Typography variant="caption" sx={{ color: '#ef4444', display: 'block' }}>
                                    Assigned to: {getGroupNameById(option.group_id)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        );
                      }}
                      sx={{ mt: 2 }}
                    />

                    <MuiFormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={(e) => handleInputChange('isActive', e.target.checked)}
                          disabled={saving}
                        />
                      }
                      label="Active"
                    />
                  </Box>
                ) : activeTab === 1 ? (
                  // Rates Tab
                  <Box sx={{ pt: 2 }}>
                    <Typography variant="h6" sx={{ mb: 3, color: '#374151', fontWeight: 600 }}>
                      {selectedGroup?.name} - Group Rates
                    </Typography>
                    
                    {/* Toggle Switches */}
                    <Box sx={{ display: 'flex', gap: 4, mb: 4 }}>
                      <MuiFormControlLabel
                        control={
                          <Switch
                            checked={ratesData?.late === 1}
                            onChange={(e, checked) => handleRatesInputChange('late', checked)}
                            disabled={saving}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#12C1DC',
                                '& + .MuiSwitch-track': {
                                  backgroundColor: '#12C1DC',
                                },
                              },
                            }}
                          />
                        }
                        label="Lates"
                        sx={{ fontWeight: 500 }}
                      />
                      <MuiFormControlLabel
                        control={
                          <Switch
                            checked={ratesData?.undertime === 1}
                            onChange={(e) => handleRatesInputChange('undertime', e.target.checked)}
                            disabled={saving}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#12C1DC',
                                '& + .MuiSwitch-track': {
                                  backgroundColor: '#12C1DC',
                                },
                              },
                            }}
                          />
                        }
                        label="Undertime"
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>

                    {/* Rates Input Fields */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                      <TextField
                        label="Daily Rate"
                        type="number"
                        value={ratesData?.daily}
                        onChange={(e) => handleRatesInputChange('daily', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Holiday Rate"
                        type="number"
                        value={ratesData?.holiday}
                        onChange={(e) => handleRatesInputChange('holiday', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Holiday OT Rate"
                        type="number"
                        value={ratesData?.holidayot}
                        onChange={(e) => handleRatesInputChange('holidayot', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Holiday Rest Day Rate"
                        type="number"
                        value={ratesData?.holidayrestday}
                        onChange={(e) => handleRatesInputChange('holidayrestday', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Holiday Rest Day OT Rate"
                        type="number"
                        value={ratesData?.holidayrestdayot}
                        onChange={(e) => handleRatesInputChange('holidayrestdayot', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Nightshift Rate"
                        type="number"
                        value={ratesData?.nightshift}
                        onChange={(e) => {
                          const value = Math.max(0, Number(e.target.value));
                          handleRatesInputChange('nightshift', value);
                        }}
                        onWheel={(e) => e.target.blur()} // 👈 prevents scroll changing value
                        disabled={saving}
                        fullWidth
                        inputProps={{
                          min: 0
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Nightshift OT Rate"
                        type="number"
                        value={ratesData?.nightshiftot}
                        onChange={(e) => handleRatesInputChange('nightshiftot', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Holiday Nightshift Rate"
                        type="number"
                        value={ratesData?.holidaynightshift}
                        onChange={(e) => handleRatesInputChange('holidaynightshift', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Holiday Nightshift OT Rate"
                        type="number"
                        value={ratesData?.holidaynightshiftot}
                        onChange={(e) => handleRatesInputChange('holidaynightshiftot', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Special Holiday Nightshift Rate"
                        type="number"
                        value={ratesData?.specialnightshift}
                        onChange={(e) => handleRatesInputChange('specialnightshift', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Special Holiday Nightshift OT Rate"
                        type="number"
                        value={ratesData?.specialnightshiftot}
                        onChange={(e) => handleRatesInputChange('specialnightshiftot', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Overtime Rate"
                        type="number"
                        value={ratesData?.overtime}
                        onChange={(e) => handleRatesInputChange('overtime', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
{/*                      <TextField
                        label="SSS Rate"
                        type="number"
                        value={ratesData.sss}
                        onChange={(e) => handleRatesInputChange('sssRate', parseFloat(e.target.value) || 0)}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />*/}
                      <TextField
                        label="Pag-ibig Rate"
                        type="number"
                        value={ratesData?.pagibig}
                        onChange={(e) => handleRatesInputChange('pagibig', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Philhealth Rate"
                        type="number"
                        value={ratesData?.philhealth}
                        onChange={(e) => handleRatesInputChange('philhealth', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Rest Day Duty Rate"
                        type="number"
                        value={ratesData?.restday}
                        onChange={(e) => handleRatesInputChange('restday', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Rest Day OT Rate"
                        type="number"
                        value={ratesData?.restdayot}
                        onChange={(e) => handleRatesInputChange('restdayot', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Special Holiday Rate"
                        type="number"
                        value={ratesData?.specialholiday}
                        onChange={(e) => handleRatesInputChange('specialholiday', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Special Holiday OT Rate"
                        type="number"
                        value={ratesData?.specialholidayot}
                        onChange={(e) => handleRatesInputChange('specialholidayot', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Special Holiday RD Rate"
                        type="number"
                        value={ratesData?.specialholidayrestday}
                        onChange={(e) => handleRatesInputChange('specialholidayrestday', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Special Holiday RD OT Rate"
                        type="number"
                        value={ratesData?.specialholidayrestdayot}
                        onChange={(e) => handleRatesInputChange('specialholidayrestdayot', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                      <TextField
                        label="Allowance"
                        type="number"
                        value={ratesData?.allowance}
                        onChange={(e) => handleRatesInputChange('allowance', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          min: 0,
                          step: "any" // Optional: allows decimals like 0.5
                        }}
                        disabled={saving}
                        fullWidth

                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                    </Box>
                  </Box>
                ) : activeTab === 2 ? (
                  // Allowances Tab
                  <Box sx={{ pt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
                        {selectedGroup?.name} - Allowances
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenAllowanceDialog('add')}
                        sx={{
                          bgcolor: '#12C1DC',
                          '&:hover': { bgcolor: '#0FABDB' },
                          textTransform: 'none'
                        }}
                      >
                        Create Allowance
                      </Button>
                    </Box>

                    {/* Search */}
                    <TextField
                      variant="outlined"
                      size="small"
                      placeholder="Search allowances..."
                      value={allowanceSearchTerm}
                      onChange={(e) => setAllowanceSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#9ca3af' }} />
                          </InputAdornment>
                        ),
                        endAdornment: allowanceSearchTerm && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setAllowanceSearchTerm('')}
                              sx={{ color: '#9ca3af' }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{ mb: 2, width: '100%' }}
                    />

                    {/* Allowances Table */}
                    {loadingAllowances ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : allowances.length === 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                          No allowances available. Create your first allowance to get started.
                        </Typography>
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Name</strong></TableCell>
                              <TableCell><strong>Amount/Rate</strong></TableCell>
                              <TableCell><strong>Description</strong></TableCell>
                              <TableCell><strong>Active</strong></TableCell>
                              <TableCell><strong>Taxable</strong></TableCell>
                              <TableCell><strong>Type</strong></TableCell>
                              <TableCell><strong>Admin Fee</strong></TableCell>
                              <TableCell><strong>Actions</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {allowances
                              .filter(allowance => {
                                const searchLower = allowanceSearchTerm.toLowerCase();
                                return allowance.name.toLowerCase().includes(searchLower) ||
                                  (allowance.description || '').toLowerCase().includes(searchLower);
                              })
                              .map((allowance) => {
                                const isAssigned = allowance.assignments?.groups?.includes(selectedGroup?.id);
                                return (
                                  <TableRow key={allowance._id} hover>
                                    <TableCell>{allowance.name}</TableCell>
{/*                                    <TableCell>
                                      <Chip
                                        label={allowance.type === 'fixed_amount' ? 'Fixed' : 'Rate'}
                                        size="small"
                                        sx={{
                                          bgcolor: allowance.type === 'fixed_amount' ? '#e3f2fd' : '#f3e5f5',
                                          color: allowance.type === 'fixed_amount' ? '#1976d2' : '#7b1fa2'
                                        }}
                                      />
                                    </TableCell>*/}
{/*                                    <TableCell>
                                      {allowance.type === 'fixed_amount'
                                        ? `₱${(allowance.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                        : `${(allowance.rate || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`
                                      }
                                    </TableCell>*/}
                                    <TableCell>
                                      {allowance.amount || 0}
                                    </TableCell>
                                    <TableCell>{allowance.description || '-'}</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={allowance.is_active ? 'Yes' : 'No'}
                                        size="small"
                                        sx={{
                                          bgcolor: allowance.is_active ? '#e3f2fd' : '#f3e5f5',
                                          color: allowance.is_active ? '#1976d2' : '#7b1fa2'
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={allowance.is_taxable ? 'Yes' : 'No'}
                                        size="small"
                                        sx={{
                                          bgcolor: allowance.is_taxable ? '#e3f2fd' : '#f3e5f5',
                                          color: allowance.is_taxable ? '#1976d2' : '#7b1fa2'
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={allowance.type === 'billing' ? 'Less to billing' : allowance.type === 'gross_billing' ? 'Add to Gross billing' : 'Charge to client'}
                                        size="small"
                                        sx={{
                                          bgcolor: allowance.type === 'billing' ? '#e3f2fd' : allowance.type === 'gross_billing' ? '#e8f5e9' : '#f3e5f5',
                                          color: allowance.type === 'billing' ? '#1976d2' : allowance.type === 'gross_billing' ? '#2e7d32' : '#7b1fa2'
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={allowance.admin ? 'Yes' : 'No'}
                                        size="small"
                                        sx={{
                                          bgcolor: allowance.admin ? '#e3f2fd' : '#f3e5f5',
                                          color: allowance.admin ? '#1976d2' : '#7b1fa2'
                                        }}
                                      />
                                    </TableCell>
{/*                                    <TableCell>
                                      <Chip
                                        label={isAssigned ? 'Yes' : 'No'}
                                        size="small"
                                        onClick={() => handleToggleAllowanceAssignment(allowance)}
                                        sx={{
                                          bgcolor: isAssigned ? '#e8f5e9' : '#e0e0e0',
                                          color: isAssigned ? '#2e7d32' : '#616161',
                                          cursor: 'pointer',
                                          '&:hover': {
                                            bgcolor: isAssigned ? '#c8e6c9' : '#d5d5d5'
                                          }
                                        }}
                                      />
                                    </TableCell>*/}
                                    <TableCell>
                                      <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Tooltip title="Edit">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleOpenAllowanceDialog('edit', allowance)}
                                            sx={{ color: '#12C1DC' }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeleteAllowance(allowance)}
                                            sx={{ color: '#ef4444' }}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            {allowances.filter(allowance => {
                              const searchLower = allowanceSearchTerm.toLowerCase();
                              return allowance.name.toLowerCase().includes(searchLower) ||
                                (allowance.description || '').toLowerCase().includes(searchLower);
                            }).length === 0 && (
                              <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    No allowances match your search
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCloseDialog}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleFormSubmit}
                  variant="contained"
                  disabled={saving}
                  sx={{
                    bgcolor: '#12C1DC',
                    '&:hover': { bgcolor: '#0FABDB' }
                  }}
                >
                  {saving ? 'Saving...' : (activeTab === 0 ? (dialogMode === 'add' ? 'Create' : 'Update') : activeTab === 1 ? 'Save Rates' : activeTab === 2 ? 'Save' : '')}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Allowance Form Dialog */}
            <Dialog
              open={allowanceDialogOpen}
              onClose={handleCloseAllowanceDialog}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>
                {allowanceDialogMode === 'add' ? 'Create New Allowance' : 'Edit Allowance'}
              </DialogTitle>
              <DialogContent>
                <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Allowance Name"
                    value={allowanceFormData.name}
                    onChange={(e) => setAllowanceFormData({ ...allowanceFormData, name: e.target.value })}
                    error={!!allowanceFormErrors.name}
                    helperText={allowanceFormErrors.name}
                    fullWidth
                    required
                    disabled={saving}
                  />
{/*
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={allowanceFormData.type}
                      onChange={(e) => setAllowanceFormData({ ...allowanceFormData, type: e.target.value })}
                      label="Type"
                      disabled={saving}
                    >
                      <MenuItem value="fixed_amount">Fixed Amount</MenuItem>
                      <MenuItem value="rate_based">Rate Based</MenuItem>
                    </Select>
                  </FormControl>*/}
                  <TextField
                    label="Amount"
                    type="number"
                    value={allowanceFormData.amount}
                    onChange={(e) => setAllowanceFormData({ ...allowanceFormData, amount: parseFloat(e.target.value) || 0 })}
                    error={!!allowanceFormErrors.amount}
                    helperText={allowanceFormErrors.amount}
                    fullWidth
                    required
                    disabled={saving}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                  {/*{allowanceFormData.type === 'fixed_amount' ? (
                    <TextField
                      label="Amount"
                      type="number"
                      value={allowanceFormData.amount}
                      onChange={(e) => setAllowanceFormData({ ...allowanceFormData, amount: parseFloat(e.target.value) || 0 })}
                      error={!!allowanceFormErrors.amount}
                      helperText={allowanceFormErrors.amount}
                      fullWidth
                      required
                      disabled={saving}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  ) : (
                    <TextField
                      label="Rate"
                      type="number"
                      value={allowanceFormData.rate}
                      onChange={(e) => setAllowanceFormData({ ...allowanceFormData, rate: parseFloat(e.target.value) || 0 })}
                      error={!!allowanceFormErrors.rate}
                      helperText={allowanceFormErrors.rate}
                      fullWidth
                      required
                      disabled={saving}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  )}
*/}
                  <TextField
                    label="Description"
                    value={allowanceFormData.description}
                    onChange={(e) => setAllowanceFormData({ ...allowanceFormData, description: e.target.value })}
                    multiline
                    rows={3}
                    fullWidth
                    disabled={saving}
                  />

                  <MuiFormControlLabel
                    control={
                      <Switch
                        checked={allowanceFormData.is_active}
                        onChange={(e) => setAllowanceFormData({ ...allowanceFormData, is_active: e.target.checked })}
                        disabled={saving}
                      />
                    }
                    label="Active"
                  />

                  <MuiFormControlLabel
                    control={
                      <Switch
                        checked={allowanceFormData.is_taxable}
                        onChange={(e) => setAllowanceFormData({ ...allowanceFormData, is_taxable: e.target.checked })}
                        disabled={saving}
                      />
                    }
                    label="Taxable"
                  />
                  <MuiFormControlLabel
                    control={
                      <Switch
                        checked={allowanceFormData.admin}
                        onChange={(e) => {
                          const newAdmin = e.target.checked;
                          setAllowanceFormData({ 
                            ...allowanceFormData, 
                            admin: newAdmin,
                            // Reset type to billing if admin is turned off while gross_billing is selected
                            type: !newAdmin && allowanceFormData.type === 'gross_billing' ? 'billing' : allowanceFormData.type
                          });
                        }}
                        disabled={saving}
                      />
                    }
                    label="Admin fee"
                  />
                  <RadioGroup
                    value={allowanceFormData.type}
                    onChange={(e) =>
                      setAllowanceFormData({
                        ...allowanceFormData,
                        type: e.target.value,
                      })
                    }
                  >
                    <FormControlLabel
                      value="client"
                      control={<Radio disabled={saving} />}
                      label="Charge to client"
                    />
                    <FormControlLabel
                      value="billing"
                      control={<Radio disabled={saving} />}
                      label="Less to billing"
                    />
                    {allowanceFormData.admin && (
                      <FormControlLabel
                        value="gross_billing"
                        control={<Radio disabled={saving} />}
                        label="Add to Gross billing"
                      />
                    )}
                  </RadioGroup>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCloseAllowanceDialog}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAllowanceFormSubmit}
                  variant="contained"
                  disabled={saving}
                  sx={{
                    bgcolor: '#12C1DC',
                    '&:hover': { bgcolor: '#0FABDB' }
                  }}
                >
                  {saving ? 'Saving...' : (allowanceDialogMode === 'add' ? 'Create' : 'Update')}
                </Button>
              </DialogActions>
            </Dialog>
          </Paper>
        </motion.div>
      </Box>
    </Fade>
  );
};

export default Groups;
